import { useContext, useMemo } from 'react';

import {
  Column,
  Icons,
  makeActionsColumn,
  TableHeader,
  tokens,
} from '@cast/design-system';

import { useAbility } from '@cast/shared-ability-data-access';
import { useSimulatedClusterDemoUi } from '@cast/infrastructure-demo';
import { useIsOrganizationReadOnly } from '@cast/split-me/hooks';
import { useNavigate } from '@cast/infrastructure-organization';
import { useCluster } from '@cast/shared-cluster-context-data-access';

import { NamespacesLinkCell } from '@cast/split-me/features/cluster/cost-report';
import { CopyToClipboard } from '@cast/split-me/components';
import {
  OptimizationWorkloadsListContext,
  canEditWorkload,
  hasPredictiveWorkloads,
  useStatusColumnWidth,
  ActionsMenu,
  DifferenceTooltip,
  OriginalRequestsCell,
  PodsCell,
  RecommendedMetricsCell,
  RequestedMetricsCell,
  SavingsTooltip,
  OptimizationTypeCell,
  StatusCell,
  WorkloadNameCell,
  DiffCell,
  SavingsCell,
  DiffSorter,
  OriginalRequestsSorter,
  RequestedSorter,
  BodyRowWithTooltip,
  PodsSorter,
  RecommendedSorter,
  WorkloadConstraintsCell,
  RecommendedColumnHeader,
  useWorkloadsTableBulkActions,
  WorkloadsTableBulkActions,
  ScalingPolicyCell,
} from '@cast/split-me/features/cluster/workload-optimization';
import { PreparedWorkload } from '@cast/split-me/features/workload-optimization';
import { useWorkloadScalingPolicies } from '@cast/split-me/features/workload-optimization/hooks';
import { FailedToLoad } from '@cast/shared-messages-ui';
import {
  NoData,
  NoResults,
  SearchableTable,
  SearchableTableRecordsCount,
} from '@cast/shared-search-ui';
import { useTrial } from '@cast/shared-trial-ui';

import { buildInventoryWorkloadViewPath } from '../../../../../../navigation';
import {
  WORKLOAD_CATEGORIES,
  WORKLOAD_VIEWS,
} from '../../../../../../_constants';

export const WorkloadsTable = () => {
  const { workloads, hasError, refetch, isNamespaceWritable } = useContext(
    OptimizationWorkloadsListContext
  );

  const navigate = useNavigate();
  const { cluster } = useCluster();

  const accountReadOnly = useIsOrganizationReadOnly();
  const [canEdit] = useAbility().canMany([
    ['update', 'ClusterWorkloadAutoscaler'],
  ]);

  const { isEligible } = useTrial();
  const { policies } = useWorkloadScalingPolicies();
  const isSimulatedClusterDemoUi = useSimulatedClusterDemoUi();

  const {
    selectedIds,
    setSelectedIds,
    toggleOptimization,
    enablePredictiveWorkloads,
    disablePredictiveWorkloads,
    canDisableEditableWorkloads,
    canEnableWorkloadsCount,
    enableBatchAutoAssignment,
    canEnableAutoAssignmentCount,
  } = useWorkloadsTableBulkActions({ workloads, hpaV2Status: 'available' });

  const showPredictiveColumn = useMemo(
    () => hasPredictiveWorkloads(workloads),
    [workloads]
  );

  const statusColumnWidth = useStatusColumnWidth(workloads);

  const getWorkloadLink = (workload: PreparedWorkload) =>
    buildInventoryWorkloadViewPath({
      clusterId: cluster.id,
      workloadId: workload.id,
      category: WORKLOAD_CATEGORIES.AUTOSCALING,
      view: WORKLOAD_VIEWS.DASHBOARD,
    });

  const columns = useMemo(
    () => (
      <>
        <Column<PreparedWorkload>
          id="id"
          header="ID"
          width={31}
          cellWrapperSx={{ pr: 0 }}
          bodyCellSx={{ '& svg': { fontSize: 16, color: 'grey.400' } }}
          renderCell={({ id }) => <CopyToClipboard text={id} />}
          stopClickPropagation
        />
        <Column<PreparedWorkload>
          id="name"
          header="Workload"
          width={200}
          renderCell={(row) => (
            <WorkloadNameCell workload={row} to={getWorkloadLink(row)} />
          )}
          cellWrapperSx={{ pl: 20, pr: 12 }}
          sortable
          resizable
          minResizeWidth={70}
        />
        <Column<PreparedWorkload>
          id="verticalScalingPolicy"
          header="Scaling policy"
          renderCell={(row) => (
            <ScalingPolicyCell
              workload={row}
              disabled={!canEdit}
              canEdit={canEdit}
              policies={policies ?? []}
            />
          )}
          minWidth={220}
          sortable
          accessor="verticalScalingPolicy.name"
          stopClickPropagation
          resizable
          minResizeWidth={70}
        />
        <Column<PreparedWorkload>
          id="optimizationType"
          header="Optimization"
          width={130}
          renderCell={OptimizationTypeCell}
        />
        <Column<PreparedWorkload>
          id="workloadConstraints"
          header="workload constraints"
          minWidth={210}
          renderCell={(workload) => (
            <WorkloadConstraintsCell workload={workload} />
          )}
          resizable
          minResizeWidth={70}
          visible={false}
        />
        <Column<PreparedWorkload>
          id="namespace"
          header="namespace"
          width={140}
          renderCell={({ namespace }) => (
            <NamespacesLinkCell namespace={namespace} />
          )}
          sortable
          resizable
          minResizeWidth={70}
        />
        <Column<PreparedWorkload>
          id="estimatedMonthlySavings"
          infoTooltip={<SavingsTooltip />}
          header="est. savings/mo"
          headerFullName="Estimated saving per month"
          width={195}
          sortable
          sortingType="number"
          renderCell={({ estimatedMonthlySavings }) => (
            <SavingsCell savings={estimatedMonthlySavings} />
          )}
          resizable
          minResizeWidth={85}
        />
        <Column<PreparedWorkload>
          id="originalRequests"
          header="Orig. Requests"
          headerFullName="Original requests"
          width={160}
          renderCell={OriginalRequestsCell}
          Sorter={OriginalRequestsSorter}
          sortable
          resizable
          minResizeWidth={70}
          visible={false}
        />
        <Column<PreparedWorkload>
          id="requested"
          header="Req."
          headerFullName="Requested"
          width={67}
          renderCell={RequestedMetricsCell}
          cellWrapperSx={{ pr: 0 }}
          Sorter={RequestedSorter}
          sortable
        />
        <Column<PreparedWorkload>
          id="recommended"
          header={<RecommendedColumnHeader />}
          headerFullName="Recommended"
          infoTooltip="Compares current resource requests with the recommended values"
          minWidth={150}
          cellWrapperSx={{ pl: 0 }}
          renderCell={RecommendedMetricsCell}
          Sorter={RecommendedSorter}
          sortable
          resizable
          minResizeWidth={65}
        />
        <Column<PreparedWorkload>
          id="podCount-vertical"
          header="Pod count"
          infoTooltip="Compares current to desired pod count"
          minWidth={144}
          renderCell={PodsCell}
          bodyCellSx={{ fontSize: 14 }}
          Sorter={PodsSorter}
          sortingType="number"
          sortable
          resizable
          minResizeWidth={85}
        />
        <Column<PreparedWorkload>
          id="diff"
          header="Difference"
          infoTooltip={<DifferenceTooltip />}
          minWidth={135}
          renderCell={(it) => <DiffCell workload={it} diffKey="diff" />}
          cellWrapperSx={{
            borderRight: tokens.theme.border.structural.neutral,
          }}
          sortable
          Sorter={DiffSorter}
          resizable
          minResizeWidth={70}
        />
        <Column<PreparedWorkload>
          id="status"
          header="Status"
          width={statusColumnWidth}
          renderCell={StatusCell}
        />
        {makeActionsColumn<PreparedWorkload>({
          action: (workload) => {
            return <ActionsMenu workloadId={workload.id} />;
          },
          width: 72,
          cellSx: {
            '& .DS-Table-ActionIcon': {
              width: 40,
              height: 40,
            },
          },
        })}
      </>
    ),
    [canEdit, policies, statusColumnWidth]
  );

  return (
    <SearchableTable
      maxHeight="80vh"
      rowHeight={48}
      hasFailed={hasError}
      components={{
        noData: <NoData icon={<Icons.CastNode />} entity="workload" />,
        noResults: <NoResults icon={<Icons.CastNode />} entity="workload" />,
        failed: (
          <FailedToLoad title="Failed to load workloads" refresh={refetch} />
        ),
      }}
      cacheKey="inventory-workloads-autoscaling-list"
      outerHeader={
        <TableHeader
          recordsCount={
            <SearchableTableRecordsCount
              count={selectedIds.length}
              title="workload"
            />
          }
          bulkActions={
            <WorkloadsTableBulkActions
              workloads={workloads}
              showHpaV2
              isWoopDemoMode={false}
              hpaV2Status="available"
              selectedIds={selectedIds}
              toggleOptimization={toggleOptimization}
              onDisabled={false}
              offDisabled={false}
              enableBatchAutoAssignment={enableBatchAutoAssignment}
              canEnableAutoAssignmentCount={canEnableAutoAssignmentCount}
              enablePredictiveWorkloads={enablePredictiveWorkloads}
              disablePredictiveWorkloads={disablePredictiveWorkloads}
              canEnableWorkloadsCount={canEnableWorkloadsCount}
              canDisableEditableWorkloads={canDisableEditableWorkloads}
              showPredictiveColumn={showPredictiveColumn}
            />
          }
        />
      }
      BodyRowComponent={BodyRowWithTooltip}
      isRowSelectable={(workload) =>
        canEditWorkload(workload) && isNamespaceWritable(workload.namespace)
      }
      selectedRows={
        canEdit && !isSimulatedClusterDemoUi ? selectedIds : undefined
      }
      onRowsSelected={
        canEdit && !isSimulatedClusterDemoUi ? setSelectedIds : undefined
      }
      checkboxColumnProps={{
        width: 24,
        cellWrapperSx: {
          pl: 8,
          pr: 0,
        },
      }}
      onRowClick={(event, { row }) =>
        navigate(getWorkloadLink(row), { clickEvent: event })
      }
      sx={{
        '& .DS-Table-HeaderRowWrapper': {
          height: 52,
        },
        '& .disabled-row .DS-Table-BodyRowWrapper': {
          opacity: 1,
          '& .DS-Table-CellWrapper': {
            backgroundColor: tokens.theme.bg.structural.surfaceSubtle,
          },
        },
        '& .disabled-row .DS-Table-rowSelectionDisabled::after': {
          display: 'none',
        },
        '& .woop-premium-value':
          isEligible && accountReadOnly
            ? {
                filter: 'blur(4px)',
              }
            : {},
      }}
      testId="woop-table"
    >
      {columns}
    </SearchableTable>
  );
};
